import {
  asClause,
  asSentence,
  ensureTerminal,
  fillFrame,
  properWords,
  splitLines,
  tidy,
} from "@/lib/tool-kit/essay/sentence";

/**
 * Claim–Evidence–Reasoning paragraphs for science classes.
 *
 * Three wordings. Each is a set of frames — an optional question sentence, the claim, a
 * lead-in to the evidence, connectors for each evidence item, the reasoning and an
 * optional closing — keyed by version so a translated page can replace any of them.
 * Evidence is a list (one observation per line), which is why it has connectors rather
 * than a single frame: "In addition," on the second item and "Finally," on the last.
 */

export type CerVersion = "standard" | "question" | "concise";

export const CER_VERSIONS: readonly CerVersion[] = [
  "standard",
  "question",
  "concise",
];

export type CerPart = "question" | "claim" | "evidence" | "reasoning";

export const CER_QUESTION_FRAMES = {
  standard: "",
  question: "{question}",
  concise: "",
} as const;

export const CER_CLAIM_FRAMES = {
  standard: "{claim}.",
  question: "The results show that {claim}.",
  concise: "Based on the evidence, {claim}.",
} as const;

export const CER_EVIDENCE_LEADS = {
  standard: "The data support this claim.",
  question: "",
  concise: "",
} as const;

/** Per version: the connector for a lone item, then the first, middle and last items. */
export const CER_CONNECTORS = {
  standard: ["", "", "In addition,", "Finally,"],
  question: ["For example,", "For example,", "In addition,", "Finally,"],
  concise: ["", "First,", "Next,", "Finally,"],
} as const;

export const CER_REASONING_FRAMES = {
  standard: "This evidence supports the claim because {reasoning}.",
  question: "These results make sense because {reasoning}.",
  concise: "The scientific explanation is that {reasoning}.",
} as const;

export const CER_CLOSING_FRAMES = {
  standard: "",
  question:
    "Because the data follow the pattern this principle predicts, they support the claim.",
  concise: "This principle accounts for the pattern in the data.",
} as const;

export interface CerFrames {
  questionFrames: Readonly<Record<string, string>>;
  claimFrames: Readonly<Record<string, string>>;
  evidenceLeads: Readonly<Record<string, string>>;
  connectors: Readonly<Record<string, readonly string[]>>;
  reasoningFrames: Readonly<Record<string, string>>;
  closingFrames: Readonly<Record<string, string>>;
}

export const CER_FRAMES: CerFrames = {
  questionFrames: CER_QUESTION_FRAMES,
  claimFrames: CER_CLAIM_FRAMES,
  evidenceLeads: CER_EVIDENCE_LEADS,
  connectors: CER_CONNECTORS,
  reasoningFrames: CER_REASONING_FRAMES,
  closingFrames: CER_CLOSING_FRAMES,
};

export interface CerInput {
  question: string;
  claim: string;
  evidence: string;
  reasoning: string;
}

export interface CerSentence {
  part: CerPart;
  text: string;
}

const RE_QUESTION_MARK = /\?["”’)]*$/;
const RE_TRAILING_STOP = /[.!…]+$/;

/** The question as a sentence that ends with a question mark. */
function questionSentence(question: string): string | null {
  const text = tidy(question);
  if (text === "") {
    return null;
  }
  if (RE_QUESTION_MARK.test(text)) {
    return asSentence(text);
  }
  return `${asSentence(text).replace(RE_TRAILING_STOP, "")}?`;
}

/** The connector for item `index` of `count`: lone, first, middle or last. */
function connectorFor(
  connectors: readonly string[],
  index: number,
  count: number
): string {
  if (count === 1) {
    return connectors[0] ?? "";
  }
  if (index === 0) {
    return connectors[1] ?? "";
  }
  if (index === count - 1 && count >= 3) {
    return connectors[3] ?? "";
  }
  return connectors[2] ?? "";
}

function evidenceSentences(
  items: readonly string[],
  connectors: readonly string[],
  proper: ReadonlySet<string>
): string[] {
  return items.map((item, index) => {
    const connector = connectorFor(connectors, index, items.length);
    if (connector === "") {
      return asSentence(item);
    }
    return ensureTerminal(`${connector} ${asClause(item, proper)}`);
  });
}

function pushFilled(
  sentences: CerSentence[],
  part: CerPart,
  template: string | undefined,
  values: Readonly<Record<string, string | null>>
): void {
  if (!template) {
    return;
  }
  const text = fillFrame(template, values);
  if (text) {
    sentences.push({ part, text });
  }
}

/** One wording of the paragraph, sentence by sentence. */
export function buildCerParagraph(
  input: CerInput,
  version: CerVersion,
  frames: CerFrames = CER_FRAMES
): CerSentence[] {
  const items = splitLines(input.evidence);
  const proper = properWords([input.question, input.claim, ...items]);
  const values = {
    claim: asClause(input.claim, proper),
    reasoning: asClause(input.reasoning, proper),
  };
  const sentences: CerSentence[] = [];
  const question = questionSentence(input.question);
  if (question && frames.questionFrames[version]) {
    sentences.push({ part: "question", text: question });
  }
  pushFilled(sentences, "claim", frames.claimFrames[version], values);
  pushFilled(sentences, "evidence", frames.evidenceLeads[version], values);
  for (const text of evidenceSentences(
    items,
    frames.connectors[version] ?? [],
    proper
  )) {
    sentences.push({ part: "evidence", text });
  }
  pushFilled(sentences, "reasoning", frames.reasoningFrames[version], values);
  pushFilled(sentences, "reasoning", frames.closingFrames[version], values);
  return sentences;
}

export type CerField = keyof CerInput;

export function missingCerFields(input: CerInput): CerField[] {
  const required: CerField[] = ["claim", "evidence", "reasoning"];
  return required.filter((field) => input[field].trim() === "");
}

/* ------------------------------------------------------------------ checks */

export type CerCheckId =
  | "claimOneSentence"
  | "claimDirect"
  | "answersQuestion"
  | "noProve"
  | "evidenceData"
  | "evidenceUnits"
  | "evidenceSeveral"
  | "evidenceRelevant"
  | "reasoningPrinciple"
  | "reasoningNotData"
  | "reasoningLength";

/** `ok`: the surface feature is there. `check`: it is missing. `manual`: a person must judge. */
export type CheckStatus = "ok" | "check" | "manual";

export interface CerCheck {
  id: CerCheckId;
  part: "claim" | "evidence" | "reasoning";
  status: CheckStatus;
}

/**
 * Checks that read English words — hedges, "prove", unit names, the question's key words
 * against an English stop list. They run only when the page locale is English; in any other
 * language they would pass or fail at random, so they are left out of the list entirely.
 */
export const ENGLISH_ONLY_CER_CHECKS: ReadonlySet<CerCheckId> = new Set([
  "claimDirect",
  "answersQuestion",
  "noProve",
  "evidenceUnits",
]);

/** Whether the English-wording checks apply to text written for this locale. */
export function cerWordingChecksApply(locale: string): boolean {
  return locale.split("-")[0].toLowerCase() === "en";
}

/**
 * A sentence ends at . ? ! (and the Devanagari danda or Arabic question mark) followed by
 * a space and a capital or caseless letter, or at a CJK full stop with no space at all.
 * A leading ¿ ¡ or opening quote is skipped over.
 */
const RE_SENTENCE_END =
  /(?:[.?!।؟۔]+["”’)»]*\s+(?=["“‘(«¿¡]*[\p{Lu}\p{Lo}])|[。！？]+[」』”’)]*\s*(?=\S))/u;
const RE_HEDGE = /\b(?:I think|I believe|I feel|I guess|in my opinion)\b/i;
const RE_PROVE = /\bprov(?:e|es|ed|en|ing)\b/i;
const RE_DECIMAL_DIGIT = /\p{Nd}/u;
const RE_ANY_DECIMAL_DIGIT = /\p{Nd}/gu;
const RE_ARABIC_DECIMAL = /[٫]/g;
const RE_ARABIC_GROUPING = /[٬]/g;
const RE_NUMBER = /\d+(?:[.,]\d+)?/g;
/** A number followed by a unit, or "per <unit>": "28 bubbles per minute", "4.5 cm", "37 °C". */
const RE_UNIT =
  /\d\s*(?:%|°\s*[CFK]?|(?:mm|cm|km|nm|µm|m|mg|kg|µg|g|ml|mL|L|l|ms|s|sec|min|h|hr|hrs|kPa|Pa|N|kN|J|kJ|W|kW|V|mV|A|mA|Hz|kHz|mol|mmol|M|ppm|ppb|lux|lx|dB|cal|kcal)\b|(?:hours?|minutes?|seconds?|days?|weeks?|months?|years?|degrees?|met(?:er|re)s?|centimet(?:er|re)s?|millimet(?:er|re)s?|grams?|kilograms?|lit(?:er|re)s?|millilit(?:er|re)s?|percent)\b)|\bper\s+(?:second|minute|hour|day|week|year|liter|litre|meter|metre|gram|trial|plant|leaf|cm|m|s|min|h)\b/i;
const RE_CONTENT_WORD = /[\p{L}\p{M}]{4,}/gu;
const RE_WHITESPACE = /\s+/;
/** Han, kana: scripts written without spaces, where a "word" is about two characters. */
const RE_UNSPACED = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/gu;

const QUESTION_STOP_WORDS: ReadonlySet<string> = new Set([
  "what",
  "which",
  "does",
  "will",
  "would",
  "when",
  "where",
  "there",
  "their",
  "this",
  "that",
  "these",
  "those",
  "with",
  "from",
  "into",
  "have",
  "much",
  "many",
  "more",
  "less",
  "affect",
  "affects",
  "effect",
  "effects",
  "change",
  "changes",
]);

function sentenceCount(text: string): number {
  const trimmed = tidy(text);
  if (trimmed === "") {
    return 0;
  }
  return trimmed.split(RE_SENTENCE_END).length;
}

/** Words in the text: space-separated tokens, plus about one word per two Han or kana characters. */
function wordCount(text: string): number {
  const unspaced = text.match(RE_UNSPACED)?.length ?? 0;
  const spaced = text
    .replace(RE_UNSPACED, " ")
    .split(RE_WHITESPACE)
    .filter((token) => token !== "").length;
  return spaced + Math.ceil(unspaced / 2);
}

/** The value of a decimal digit from any script: Arabic-Indic, Devanagari, fullwidth, … */
function digitValue(char: string): number {
  const codePoint = char.codePointAt(0) ?? 0;
  let offset = 0;
  while (RE_DECIMAL_DIGIT.test(String.fromCodePoint(codePoint - 1 - offset))) {
    offset += 1;
  }
  return offset % 10;
}

/** Text with every script's digits as 0-9 and Arabic decimal marks as "." and "," */
export function asciiDigits(text: string): string {
  return text
    .replace(RE_ARABIC_DECIMAL, ".")
    .replace(RE_ARABIC_GROUPING, ",")
    .replace(RE_ANY_DECIMAL_DIGIT, (char) => String(digitValue(char)));
}

/** Whether the text contains a digit in any script ("28", "٢٨", "२८", "２８"). */
function hasNumber(text: string): boolean {
  return RE_DECIMAL_DIGIT.test(text);
}

function contentWords(text: string): Set<string> {
  const words = new Set<string>();
  for (const match of text.toLowerCase().matchAll(RE_CONTENT_WORD)) {
    if (!QUESTION_STOP_WORDS.has(match[0])) {
      words.add(match[0]);
    }
  }
  return words;
}

function sharesContentWord(a: string, b: string): boolean {
  const left = contentWords(a);
  for (const word of contentWords(b)) {
    if (left.has(word)) {
      return true;
    }
  }
  return false;
}

/**
 * Numbers of two or more characters that the reasoning copies from the evidence. "28,5" and
 * "28.5" are the same number, and so are "28" and "٢٨".
 */
function repeatsData(evidence: string, reasoning: string): boolean {
  const significant = (text: string) =>
    new Set(
      [...asciiDigits(text).matchAll(RE_NUMBER)]
        .map((match) => match[0].replace(",", "."))
        .filter((value) => value.length >= 2)
    );
  const fromEvidence = significant(evidence);
  for (const value of significant(reasoning)) {
    if (fromEvidence.has(value)) {
      return true;
    }
  }
  return false;
}

function status(condition: boolean): CheckStatus {
  return condition ? "ok" : "check";
}

function questionStatus(question: string, claim: string): CheckStatus {
  if (question.trim() === "") {
    return "manual";
  }
  return status(sharesContentWord(question, claim));
}

function unitStatus(evidence: string): CheckStatus {
  if (!hasNumber(evidence)) {
    return "manual";
  }
  return status(RE_UNIT.test(evidence));
}

/**
 * Surface checks for each part. They read words and numbers, not meaning.
 *
 * The checks in `ENGLISH_ONLY_CER_CHECKS` are returned only for English; every other check
 * works on any language (sentence ends, digits in any script, decimal commas, counts).
 */
export function cerChecks(input: CerInput, locale = "en"): CerCheck[] {
  const items = splitLines(input.evidence);
  const everything = `${input.claim} ${input.evidence} ${input.reasoning}`;
  const checks: CerCheck[] = [
    {
      id: "claimOneSentence",
      part: "claim",
      status: status(sentenceCount(input.claim) === 1),
    },
    {
      id: "claimDirect",
      part: "claim",
      status: status(!RE_HEDGE.test(input.claim)),
    },
    {
      id: "answersQuestion",
      part: "claim",
      status: questionStatus(input.question, input.claim),
    },
    {
      id: "noProve",
      part: "claim",
      status: status(!RE_PROVE.test(everything)),
    },
    {
      id: "evidenceData",
      part: "evidence",
      status: status(hasNumber(input.evidence)),
    },
    {
      id: "evidenceUnits",
      part: "evidence",
      status: unitStatus(input.evidence),
    },
    {
      id: "evidenceSeveral",
      part: "evidence",
      status: status(items.length >= 2),
    },
    { id: "evidenceRelevant", part: "evidence", status: "manual" },
    { id: "reasoningPrinciple", part: "reasoning", status: "manual" },
    {
      id: "reasoningNotData",
      part: "reasoning",
      status: status(!repeatsData(input.evidence, input.reasoning)),
    },
    {
      id: "reasoningLength",
      part: "reasoning",
      status: status(wordCount(tidy(input.reasoning)) >= 12),
    },
  ];
  if (cerWordingChecksApply(locale)) {
    return checks;
  }
  return checks.filter((check) => !ENGLISH_ONLY_CER_CHECKS.has(check.id));
}
